// Section layouts: the block library of an app and its sections.json, the
// way Dunea reads, validates and edits them. One implementation for studio
// (owner endpoints), the Dunea AI (list_blocks / update_sections) and the
// workbench Sections panel, so all three agree on what a valid layout is.
// Pure and isomorphic (no Node APIs). Contract: ./primitives/blocks.

import {
  BLOCKS_DIR,
  blockSchemaSchema,
  sectionsFileSchema,
  type BlockInstance,
  type BlockPackage,
  type BlockSchema,
  type PageType,
  type SectionsFile,
} from './primitives/blocks';
import { validateFields, withFieldDefaults, type FieldDefinition } from './primitives/fields';

/** Blocks the Dunea AI writes for this app. */
export const LOCAL_BLOCK_NAMESPACE = 'local';
/** The applied theme's blocks (written by apply_theme only). */
export const THEME_BLOCK_NAMESPACE = 'theme';
/** Namespaces that are never a marketplace listing slug. */
export const RESERVED_BLOCK_NAMESPACES: readonly string[] = [LOCAL_BLOCK_NAMESPACE, THEME_BLOCK_NAMESPACE];

export type BlockSource = 'local' | 'theme' | 'marketplace';

export interface LibraryBlock {
  /** `<ns>/<name>`, what sections.json instances reference. */
  type: string;
  namespace: string;
  name: string;
  source: BlockSource;
  schema: BlockSchema;
  /** The component file (`<name>.tsx`) exists next to the schema. */
  hasCode: boolean;
}

export interface LayoutIssue {
  path: string;
  message: string;
}

export function blockSourceOf(namespace: string): BlockSource {
  if (namespace === LOCAL_BLOCK_NAMESPACE) {
    return 'local';
  }

  return namespace === THEME_BLOCK_NAMESPACE ? 'theme' : 'marketplace';
}

function projectRelative(path: string): string {
  return path
    .replace(/\\/g, '/')
    .replace(/^\/home\/project\//, '')
    .replace(/^\.?\//, '');
}

const BLOCK_FILE_RE = /^frontend\/src\/blocks\/([a-z0-9-]+)\/([a-z][a-z0-9-]*)\.(tsx|block\.json)$/;

/** `frontend/src/blocks/<ns>/<name>.tsx|.block.json` → its parts; null for anything else. Accepts pod paths. */
export function parseBlockPath(path: string): { namespace: string; name: string; file: 'code' | 'schema' } | null {
  const m = BLOCK_FILE_RE.exec(projectRelative(path));

  return m ? { namespace: m[1], name: m[2], file: m[3] === 'tsx' ? 'code' : 'schema' } : null;
}

/** The namespace directory a path is under (`frontend/src/blocks/<ns>/…`), or null outside the blocks dir. */
export function blockNamespaceOfPath(path: string): string | null {
  const m = /^frontend\/src\/blocks\/([^/]+)\//.exec(projectRelative(path));

  return m ? m[1] : null;
}

export function blockFilePaths(namespace: string, name: string): { code: string; schema: string } {
  return { code: `${BLOCKS_DIR}/${namespace}/${name}.tsx`, schema: `${BLOCKS_DIR}/${namespace}/${name}.block.json` };
}

/** The `.block.json` written for a packaged block (everything but the code). */
export function blockSchemaFileContent(block: BlockSchema | BlockPackage): string {
  const { code: _code, ...schema } = block as BlockPackage;

  return `${JSON.stringify(schema, null, 2)}\n`;
}

/** Library from the files under frontend/src/blocks (schema files with content, code files by path). */
export function buildBlockLibrary(files: readonly { path: string; content?: string | null }[]): {
  blocks: LibraryBlock[];
  problems: LayoutIssue[];
} {
  const code = new Set<string>();
  const schemas: { path: string; namespace: string; name: string; content: string }[] = [];
  const problems: LayoutIssue[] = [];

  for (const f of files) {
    const parsed = parseBlockPath(f.path);

    if (!parsed) {
      continue;
    }

    if (parsed.file === 'code') {
      code.add(`${parsed.namespace}/${parsed.name}`);
    } else {
      schemas.push({
        path: projectRelative(f.path),
        namespace: parsed.namespace,
        name: parsed.name,
        content: f.content ?? '',
      });
    }
  }

  const blocks: LibraryBlock[] = [];

  for (const s of schemas) {
    let json: unknown;

    try {
      json = JSON.parse(s.content);
    } catch {
      problems.push({ path: s.path, message: 'is not valid JSON' });
      continue;
    }

    const parsed = blockSchemaSchema.safeParse(json);

    if (!parsed.success) {
      problems.push({ path: s.path, message: zodMessage(parsed.error.issues) });
      continue;
    }

    if (parsed.data.name !== s.name) {
      problems.push({ path: s.path, message: `"name" is "${parsed.data.name}" but the file is ${s.name}.block.json` });
      continue;
    }

    const type = `${s.namespace}/${s.name}`;

    if (!code.has(type)) {
      problems.push({ path: s.path, message: `has no component: create ${BLOCKS_DIR}/${type}.tsx` });
    }

    blocks.push({
      type,
      namespace: s.namespace,
      name: s.name,
      source: blockSourceOf(s.namespace),
      schema: parsed.data,
      hasCode: code.has(type),
    });
  }

  blocks.sort((a, b) => a.type.localeCompare(b.type));

  return { blocks, problems };
}

function zodMessage(issues: readonly { path: readonly PropertyKey[]; message: string }[]): string {
  return issues
    .slice(0, 5)
    .map((i) => `${i.path.length ? `${i.path.map(String).join('.')}: ` : ''}${i.message}`)
    .join('; ');
}

// ─── sections.json ───────────────────────────────────────────────────────────

export function emptySectionsFile(): SectionsFile {
  return { version: 1, groups: {}, embeds: {} };
}

/** sections.json as written to disk (stable 2-space JSON, trailing newline). */
export function serializeSectionsFile(doc: SectionsFile): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/**
 * Version of a file's content for optimistic concurrency (ETag). Pure JS
 * (FNV-1a, two seeds) so it runs the same in studio, the AI and the browser.
 * `null` (no file yet) has its own version.
 */
export function contentVersion(content: string | null): string {
  if (content === null) {
    return 'none';
  }

  let a = 0x811c9dc5;
  let b = 0x01000193 ^ content.length;

  for (let i = 0; i < content.length; i++) {
    const c = content.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x5bd1e995) >>> 0;
  }

  return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

/** Every block type a layout references (sections, child blocks, embeds). */
export function layoutTypes(doc: SectionsFile): Set<string> {
  const out = new Set<string>(Object.keys(doc.embeds ?? {}));

  walkInstances(doc, (inst) => {
    out.add(inst.type);
  });

  return out;
}

/** Every instance id in a layout. */
export function layoutInstanceIds(doc: SectionsFile): Set<string> {
  const out = new Set<string>();

  walkInstances(doc, (inst) => {
    out.add(inst.id);
  });

  return out;
}

function walkInstances(
  doc: SectionsFile,
  fn: (inst: BlockInstance, parent: BlockInstance | null, group: string) => void,
) {
  const walk = (list: readonly BlockInstance[], parent: BlockInstance | null, group: string) => {
    for (const inst of list) {
      fn(inst, parent, group);
      walk(inst.blocks ?? [], inst, group);
    }
  };

  for (const [group, g] of Object.entries(doc.groups ?? {})) {
    walk(g.sections, null, group);
  }
}

function libraryMap(
  library: readonly LibraryBlock[] | ReadonlyMap<string, LibraryBlock>,
): ReadonlyMap<string, LibraryBlock> {
  return library instanceof Map ? library : new Map((library as readonly LibraryBlock[]).map((b) => [b.type, b]));
}

/** Whether a section accepts a child block type ('*', the full type, or a bare name in its own namespace). */
export function sectionAccepts(section: LibraryBlock, childType: string): boolean {
  return section.schema.accepts.some((a) => a === '*' || a === childType || `${section.namespace}/${a}` === childType);
}

export interface ValidateLayoutOptions {
  /**
   * Types that may be missing from the library without failing (they render
   * nothing). Owners saving a layout pass the types already in the saved file,
   * so a deleted block never locks them out of editing.
   */
  allowUnknown?: Iterable<string>;
}

export type LayoutValidation =
  | { ok: true; document: SectionsFile; warnings: LayoutIssue[] }
  | { ok: false; errors: LayoutIssue[]; warnings: LayoutIssue[] };

/**
 * Validate a sections.json document: the file schema, unique instance ids,
 * known block types of the right kind, accepted child blocks, and every
 * instance's settings against its block's fields (defaults applied first,
 * exactly like the site runtime).
 */
export function validateSectionsLayout(
  input: unknown,
  library: readonly LibraryBlock[] | ReadonlyMap<string, LibraryBlock>,
  options: ValidateLayoutOptions = {},
): LayoutValidation {
  const parsed = sectionsFileSchema.safeParse(input);

  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues
        .slice(0, 20)
        .map((i) => ({ path: i.path.map(String).join('.') || '(root)', message: i.message })),
      warnings: [],
    };
  }

  const doc = parsed.data;
  const byType = libraryMap(library);
  const allowUnknown = new Set(options.allowUnknown ?? []);
  const errors: LayoutIssue[] = [];
  const warnings: LayoutIssue[] = [];
  const ids = new Set<string>();

  const checkSettings = (block: LibraryBlock, settings: Record<string, unknown>, path: string) => {
    const defs = block.schema.settings as FieldDefinition[];
    const result = validateFields(defs, withFieldDefaults(defs, settings));

    if (!result.ok) {
      for (const [key, message] of Object.entries(result.errors)) {
        errors.push({ path: `${path}.settings.${key}`, message });
      }
    }
  };

  const unknown = (type: string, path: string) => {
    if (allowUnknown.has(type)) {
      warnings.push({ path, message: `"${type}" is not in the block library; it renders nothing` });
    } else {
      errors.push({ path, message: `unknown block "${type}" (not in the block library)` });
    }
  };

  const checkInstance = (
    inst: BlockInstance,
    path: string,
    expected: 'section' | 'block',
    parent: LibraryBlock | null,
  ) => {
    if (ids.has(inst.id)) {
      errors.push({ path: `${path}.id`, message: `duplicate id "${inst.id}"` });
    }

    ids.add(inst.id);

    if (parent && !sectionAccepts(parent, inst.type)) {
      errors.push({ path, message: `${parent.schema.title} does not accept "${inst.type}"` });
    }

    const block = byType.get(inst.type);

    if (!block) {
      unknown(inst.type, path);
      return;
    }

    if (block.schema.kind !== expected) {
      errors.push({ path, message: `"${inst.type}" is a ${block.schema.kind}, not a ${expected}` });
    }

    checkSettings(block, inst.settings ?? {}, path);

    const children = inst.blocks ?? [];

    if (children.length) {
      if (block.schema.kind !== 'section' || block.schema.accepts.length === 0) {
        errors.push({ path: `${path}.blocks`, message: `${block.schema.title} does not accept blocks` });
      } else if (block.schema.maxBlocks && children.length > block.schema.maxBlocks) {
        errors.push({
          path: `${path}.blocks`,
          message: `${block.schema.title} takes at most ${block.schema.maxBlocks} blocks`,
        });
      }

      children.forEach((child, j) => checkInstance(child, `${path}.blocks.${j}`, 'block', block));
    }
  };

  for (const [group, g] of Object.entries(doc.groups)) {
    g.sections.forEach((s, i) => checkInstance(s, `groups.${group}.sections.${i}`, 'section', null));
  }

  for (const [type, embed] of Object.entries(doc.embeds)) {
    const path = `embeds.${type}`;
    const block = byType.get(type);

    if (!block) {
      unknown(type, path);
      continue;
    }

    if (block.schema.kind !== 'embed') {
      errors.push({ path, message: `"${type}" is a ${block.schema.kind}, not an embed` });
    }

    checkSettings(block, embed.settings ?? {}, path);
  }

  return errors.length ? { ok: false, errors, warnings } : { ok: true, document: doc, warnings };
}

// ─── edits ───────────────────────────────────────────────────────────────────

export interface NewBlockInput {
  type: string;
  settings?: Record<string, unknown>;
}

/** Edit operations on a layout. Ids are unique across the whole file, so `update`/`move`/`remove` work on sections and child blocks alike. */
export type LayoutOp =
  | {
      op: 'add_section';
      group: string;
      type: string;
      /** Name of one of the block's presets (settings + child blocks to start from). */
      preset?: string;
      settings?: Record<string, unknown>;
      blocks?: NewBlockInput[];
      /** Position in the group (default: end). */
      index?: number;
      id?: string;
      /** For a new group. */
      pageType?: PageType;
    }
  | { op: 'add_block'; parentId: string; type: string; settings?: Record<string, unknown>; index?: number; id?: string }
  | {
      op: 'update';
      id: string;
      /** Merged into the current settings; `null` removes a key. */
      settings?: Record<string, unknown>;
      /** Replace the settings instead of merging. */
      replaceSettings?: boolean;
      hidden?: boolean;
    }
  | { op: 'move'; id: string; index: number; /** Sections only: move to another group. */ group?: string }
  | { op: 'remove'; id: string }
  | { op: 'set_group'; group: string; pageType: PageType }
  | { op: 'remove_group'; group: string }
  | { op: 'set_embed'; type: string; enabled: boolean; settings?: Record<string, unknown> };

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/** A fresh id for an instance of `type`: its name, then name-2, name-3… */
export function newInstanceId(type: string, taken: ReadonlySet<string>): string {
  const base = (type.split('/').pop() ?? 'section').replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 50) || 'section';

  if (!taken.has(base)) {
    return base;
  }

  for (let n = 2; ; n++) {
    const id = `${base}-${n}`;

    if (!taken.has(id)) {
      return id;
    }
  }
}

/** A new instance of a library block, from one of its presets (or its first, or none). */
export function instanceFromBlock(
  block: LibraryBlock,
  taken: Set<string>,
  opts: { preset?: string; settings?: Record<string, unknown>; blocks?: NewBlockInput[]; id?: string } = {},
): BlockInstance {
  const preset = opts.preset
    ? block.schema.presets.find((p) => p.name === opts.preset)
    : opts.settings || opts.blocks
      ? undefined
      : block.schema.presets[0];

  if (opts.preset && !preset) {
    throw new Error(`${block.schema.title} has no preset "${opts.preset}"`);
  }

  const id = opts.id && !taken.has(opts.id) ? opts.id : newInstanceId(block.type, taken);
  taken.add(id);

  const children = (opts.blocks ?? preset?.blocks ?? []).map((c) => {
    const childType = c.type.includes('/') ? c.type : `${block.namespace}/${c.type}`;
    const childId = newInstanceId(childType, taken);
    taken.add(childId);

    return { id: childId, type: childType, settings: { ...(c.settings ?? {}) } };
  });

  return {
    id,
    type: block.type,
    settings: { ...(preset?.settings ?? {}), ...(opts.settings ?? {}) },
    ...(children.length ? { blocks: children } : {}),
  };
}

interface Located {
  list: BlockInstance[];
  index: number;
  group: string;
  parent: BlockInstance | null;
}

function locate(doc: SectionsFile, id: string): Located | null {
  const search = (list: BlockInstance[], group: string, parent: BlockInstance | null): Located | null => {
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === id) {
        return { list, index: i, group, parent };
      }

      const found = search(list[i].blocks ?? [], group, list[i]);

      if (found) {
        return found;
      }
    }

    return null;
  };

  for (const [group, g] of Object.entries(doc.groups)) {
    const found = search(g.sections, group, null);

    if (found) {
      return found;
    }
  }

  return null;
}

function insertAt<T>(list: T[], item: T, index?: number): void {
  const i = index === undefined ? list.length : Math.max(0, Math.min(list.length, Math.floor(index)));
  list.splice(i, 0, item);
}

/**
 * Apply edits to a layout. Never mutates `doc`. Ops run in order; one that
 * can't apply (unknown id, unknown block…) is reported and skipped. Validate
 * the result with validateSectionsLayout before writing it.
 */
export function applyLayoutOps(
  doc: SectionsFile,
  ops: readonly LayoutOp[],
  library: readonly LibraryBlock[] | ReadonlyMap<string, LibraryBlock> = [],
): { document: SectionsFile; errors: string[] } {
  const next = clone(doc);
  next.groups ??= {};
  next.embeds ??= {};

  const byType = libraryMap(library);
  const errors: string[] = [];
  const taken = layoutInstanceIds(next);

  // One op; returns why it couldn't apply, or null.
  const applyOne = (op: LayoutOp): string | null => {
    switch (op.op) {
      case 'add_section': {
        const block = byType.get(op.type);

        if (!block) {
          return `unknown block "${op.type}"`;
        }

        let inst: BlockInstance;

        try {
          inst = instanceFromBlock(block, taken, op);
        } catch (err) {
          return (err as Error).message;
        }

        next.groups[op.group] ??= { pageType: op.pageType ?? 'any', sections: [] };
        insertAt(next.groups[op.group].sections, inst, op.index);

        return null;
      }
      case 'add_block': {
        const at = locate(next, op.parentId);

        if (!at) {
          return `no section "${op.parentId}"`;
        }

        const parent = at.list[at.index];
        const block = byType.get(op.type);

        if (!block) {
          return `unknown block "${op.type}"`;
        }

        const id = op.id && !taken.has(op.id) ? op.id : newInstanceId(op.type, taken);
        taken.add(id);
        parent.blocks ??= [];
        insertAt(parent.blocks, { id, type: op.type, settings: { ...(op.settings ?? {}) } }, op.index);

        return null;
      }
      case 'update': {
        const at = locate(next, op.id);

        if (!at) {
          return `no section or block "${op.id}"`;
        }

        const inst = at.list[at.index];

        if (op.settings) {
          const merged: Record<string, unknown> = op.replaceSettings ? {} : { ...inst.settings };

          for (const [k, v] of Object.entries(op.settings)) {
            if (v === null) {
              delete merged[k];
            } else {
              merged[k] = v;
            }
          }

          inst.settings = merged;
        }

        if (op.hidden !== undefined) {
          if (op.hidden) {
            inst.hidden = true;
          } else {
            delete inst.hidden;
          }
        }

        return null;
      }
      case 'move': {
        const at = locate(next, op.id);

        if (!at) {
          return `no section or block "${op.id}"`;
        }

        const [inst] = at.list.splice(at.index, 1);

        if (op.group && op.group !== at.group) {
          if (at.parent) {
            at.list.splice(at.index, 0, inst);
            return 'only sections can move to another group';
          }

          if (!next.groups[op.group]) {
            at.list.splice(at.index, 0, inst);
            return `no group "${op.group}"`;
          }

          insertAt(next.groups[op.group].sections, inst, op.index);

          return null;
        }

        insertAt(at.list, inst, op.index);

        return null;
      }
      case 'remove': {
        const at = locate(next, op.id);

        if (!at) {
          return `no section or block "${op.id}"`;
        }

        at.list.splice(at.index, 1);

        if (at.parent && at.parent.blocks?.length === 0) {
          delete at.parent.blocks;
        }

        return null;
      }
      case 'set_group': {
        if (next.groups[op.group]) {
          next.groups[op.group].pageType = op.pageType;
        } else {
          next.groups[op.group] = { pageType: op.pageType, sections: [] };
        }

        return null;
      }
      case 'remove_group': {
        if (!next.groups[op.group]) {
          return `no group "${op.group}"`;
        }

        delete next.groups[op.group];

        return null;
      }
      case 'set_embed': {
        const prev = next.embeds[op.type];
        next.embeds[op.type] = { enabled: op.enabled, settings: { ...(prev?.settings ?? {}), ...(op.settings ?? {}) } };

        return null;
      }
      default:
        return 'unknown op';
    }
  };

  ops.forEach((op, n) => {
    const error = applyOne(op);

    if (error) {
      errors.push(`op ${n + 1} (${op.op}): ${error}`);
    }
  });

  return { document: next, errors };
}

/**
 * Drop every section, child block and embed whose type matches (an
 * uninstalled plugin's blocks). Never mutates `doc`.
 */
export function pruneLayoutTypes(
  doc: SectionsFile,
  remove: (type: string) => boolean,
): { document: SectionsFile; removed: number } {
  const next = clone(doc);
  let removed = 0;

  const prune = (list: BlockInstance[]): BlockInstance[] =>
    list.flatMap((inst) => {
      if (remove(inst.type)) {
        removed++;
        return [];
      }

      if (inst.blocks) {
        inst.blocks = prune(inst.blocks);

        if (inst.blocks.length === 0) {
          delete inst.blocks;
        }
      }

      return [inst];
    });

  for (const g of Object.values(next.groups ?? {})) {
    g.sections = prune(g.sections);
  }

  for (const type of Object.keys(next.embeds ?? {})) {
    if (remove(type)) {
      delete next.embeds[type];
      removed++;
    }
  }

  return { document: next, removed };
}

/**
 * Add a theme's preset groups, ONLY for groups the app doesn't have yet (an
 * owner's existing page is never overwritten). Instance ids that clash with
 * ids already in the file get fresh ones.
 */
export function addPresetGroups(
  doc: SectionsFile,
  presets: readonly { group: string; pageType: PageType; sections: readonly BlockInstance[] }[],
): { document: SectionsFile; added: string[]; skipped: string[] } {
  const next = clone(doc);
  next.groups ??= {};
  next.embeds ??= {};

  const taken = layoutInstanceIds(next);
  const added: string[] = [];
  const skipped: string[] = [];

  const reId = (inst: BlockInstance): BlockInstance => {
    const id = taken.has(inst.id) ? newInstanceId(inst.type, taken) : inst.id;
    taken.add(id);

    return {
      ...clone(inst),
      id,
      settings: clone(inst.settings ?? {}),
      ...(inst.blocks?.length ? { blocks: inst.blocks.map(reId) } : {}),
    };
  };

  for (const p of presets) {
    if (next.groups[p.group]) {
      skipped.push(p.group);
      continue;
    }

    next.groups[p.group] = { pageType: p.pageType, sections: p.sections.map(reId) };
    added.push(p.group);
  }

  return { document: next, added, skipped };
}

/** One line per block for an AI prompt or a log: type, kind, title, settings and presets. */
export function describeLibraryBlock(b: LibraryBlock): string {
  const s = b.schema;
  const settings = s.settings
    .map(
      (f) =>
        `${f.key}:${f.type}${f.list ? '[]' : ''}${f.required ? '!' : ''}${f.type === 'select' && f.options ? `(${f.options.map((o) => o.value).join('|')})` : ''}`,
    )
    .join(', ');
  const parts = [
    `- ${b.type} (${s.kind}, ${b.source}${b.hasCode ? '' : ', NO COMPONENT FILE'}): ${s.title}`,
    s.description ? `  ${s.description}` : '',
    settings ? `  settings: ${settings}` : '  settings: none',
    s.accepts.length ? `  accepts: ${s.accepts.join(', ')}${s.maxBlocks ? ` (max ${s.maxBlocks})` : ''}` : '',
    s.pageTypes.length && !(s.pageTypes.length === 1 && s.pageTypes[0] === 'any')
      ? `  pages: ${s.pageTypes.join(', ')}`
      : '',
    s.presets.length ? `  presets: ${s.presets.map((p) => p.name).join(', ')}` : '',
  ];

  return parts.filter(Boolean).join('\n');
}
